import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Drawer, Space, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import * as BillApi from '@/api/bill';
import { setSettlementAction } from '@/redux/actions/bill';
import {
  togglePartSettleDrawerVisible,
  initPartSettleInfo,
  resetPartSettleInfo,
} from '@/redux/actions/bill/partSettle';
import { parseFen2Currency } from '@/utils/number';

import Payment from '@/components/Payment';
import PayModal from '@/components/Payment/payModal';
import { PayResult, PayModalRef, PayWay } from '@/components/Payment/interface';

import BillRefund from '../refund';
import commonStyles from '../common.module.scss';

import PartSettleTable from './table';
import PartSettleInfo from './info';
import styles from './index.module.scss';

function PartSettle(props) {
  const {
    resvNo,
    selectedBills,
    selectedBillIds,
    drawerVisible,
    infoLoading,
    partSettleInfo,
    // settlementNo,
    settlementAction,
  } = props;

  const payModalRef = useRef<PayModalRef>();

  const defaultRefundAmounts = {
    roomRate: 0, // 房费
    deposit: 0, // 押金
  };

  // Payment & PayModal props
  const [initPayTypes, setInitPayTypes] = useState(false);
  const [payType, setPayType] = useState(3);
  const [certNo, setCertNo] = useState('');
  const [wyuid, setWyuid] = useState('');

  // self states
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [refundAmounts, setRefundAmounts] = useState(defaultRefundAmounts);

  const [settlementNo, setSettlementNo] = useState(''); // 结账单号
  const [refundDrawerVisible, setRefundDrawerVisible] = useState(false);

  const payResultCallback = (payResult: PayResult) => {
    if (payResult.payed && payResult.result) {
      message.success(intl.get('bill_part_settle_success'));
      props.togglePartSettleDrawerVisible(false);
      // parent component callback
      if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
    }

    if (payResult.payed && !payResult.result) {
      message.success(intl.get('bill_part_settle_fail'));
    }
  };

  const resetPayModalProps = () => {
    setInitPayTypes(false);
    setCertNo('');
  };

  const initInfo = () => {
    if (selectedBillIds) props.initPartSettleInfo(selectedBillIds);
  };

  const changeVisible = (visible) => {
    if (visible) {
      initInfo();
      setInitPayTypes(true);
    } else {
      // reset states and store
      resetPayModalProps();
      props.resetPartSettleInfo();
    }
  };

  const closeDrawer = () => {
    props.togglePartSettleDrawerVisible(false);
    setSettlementNo('');
    setRefundDrawerVisible(false);
  };

  // 生成结账单
  const generatePartSettleOrder = () => {
    const params = {
      amount: partSettleInfo.settleAmount, // 金额
      resvNo, // 预订单号
      deposit: 0, // 应退押金
      itemNos: selectedBillIds, // 子账单号list
      trigger: '执行部分结账时',
      operationPage: '部分结账抽屉',
    };
    return BillApi.generateSettleOrder(params);
  };

  const submit = async () => {
    try {
      setSubmitLoading(true);
      const res = await generatePartSettleOrder();

      if (res && res.status) {
        try {
          const result = await BillApi.confirmSettle(res.data?.no);

          if (result && result.status) {
            message.success(intl.get('bill_part_settle_success'));
            closeDrawer();

            // parent component callback
            if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
          } else {
            message.error(result.msg || intl.get('bill_part_settle_fail'));
          }
          setSubmitLoading(false);
        } catch (err) {
          setSubmitLoading(false);
        }
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
        setSubmitLoading(false);
      }
    } catch (err) {
      setSubmitLoading(false);
    }
  };

  // 收款，唤起收银台
  const gotoPay = async (confirm?: boolean) => {
    // 付款单需confirm后下单
    if (!confirm && payType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType,
        showFukuanDanConfirm: true,
      });
      return;
    }
    try {
      setPayLoading(true);
      const res = await generatePartSettleOrder();

      if (res && res.status) {
        // store settlementNo & settlementAction
        setSettlementNo(res.data?.no);
        props.setSettlementAction('pay');

        // open payModal
        payModalRef.current.openModal({
          payAmount: +partSettleInfo.settleAmount,
          payType,
          payOrderType: 102,
          paymentNo: res.data?.paymentNo,
          certNo,
          wyuid,
          extraParams: {
            trigger: '执行部分结账时',
            operationPage: '部分结账抽屉',
          },
        });
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
      }

      setPayLoading(false);
    } catch (err) {
      setPayLoading(false);
    }
  };

  // 退款，打开退款抽屉
  const gotoRefund = async () => {
    try {
      setRefundLoading(true);
      const res = await generatePartSettleOrder();

      if (res && res.status) {
        // set refund amounts
        setRefundAmounts({
          roomRate: res.data?.amount,
          deposit: 0,
        });

        // store settlementNo & settlementAction
        setSettlementNo(res.data?.no);
        setRefundDrawerVisible(true);
        props.setSettlementAction('refund');
        props.togglePartSettleDrawerVisible(false);
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
      }

      setRefundLoading(false);
    } catch (err) {
      setRefundLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      // reset states and store when unmounted
      resetPayModalProps();
      props.resetPartSettleInfo();
    };
  }, []);

  const dynamicButton = () => {
    if (partSettleInfo.settleAmount === 0) {
      return (
        <Button type="primary" loading={submitLoading} onClick={() => submit()}>
          {intl.get('bill_part_refund_confirm_settle')}
        </Button>
      );
    }
    if (partSettleInfo.settleAmount > 0) {
      return (
        <Button type="primary" danger loading={refundLoading} onClick={() => gotoRefund()}>
          {intl.get('bill_config_refound')}
        </Button>
      );
    }
    if (partSettleInfo.settleAmount < 0) {
      return (
        <Button type="primary" loading={payLoading} onClick={() => gotoPay()}>
          {intl.get('bill_credit_collection')}
        </Button>
      );
    }

    return null;
  };

  const drawerFooter = () => {
    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {dynamicButton()}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  const renderPayDesk = () => {
    if (!partSettleInfo || partSettleInfo.settleAmount === 0) return null;
    const { settleAmount } = partSettleInfo;

    return (
      <dl className={styles['partSettle-pay']}>
        <dt className={commonStyles['bill-block-title']}>{intl.get('bill_credit_cashier')}</dt>
        <dd className={styles['partSettle-pay-item']}>
          <label className="text-colon">
            {settleAmount > 0
              ? intl.get('bill_part_refund_avalible_refund_amount')
              : intl.get('bill_part_refund_avalible_receive_amount')}
          </label>
          {settleAmount > 0 ? (
            <span className="amount-refund">{parseFen2Currency(Math.abs(settleAmount))}</span>
          ) : (
            <span className="amount-pay">{parseFen2Currency(Math.abs(settleAmount))}</span>
          )}
        </dd>
        {settleAmount < 0 ? (
          <dd>
            <Payment
              excludePayWay={[PayWay.Fukuandan]}
              isInitPayTypes={initPayTypes}
              selectPayType={(type) => setPayType(type)}
              resvNo={resvNo}
              setIdCardNo={(no) => setCertNo(no)}
              setWyuid={(uid) => setWyuid(uid)}
            />
          </dd>
        ) : (
          ''
        )}
      </dl>
    );
  };

  return (
    <Drawer
      width="83.33%"
      open={drawerVisible}
      title={intl.get('bill_part_refund_settle_title')}
      maskClosable={false}
      closable={false}
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <PayModal
        ref={payModalRef}
        payResultCallback={(payResult) => payResultCallback(payResult)}
        onConfirmOk={() => gotoPay(true)}
      />
      <BillRefund
        refundDrawerVisible={refundDrawerVisible}
        refundAmounts={refundAmounts}
        settlementNo={settlementNo}
        settlementAction={settlementAction}
        refundCallback={(status) => {
          setRefundDrawerVisible(false);
          props.togglePartSettleDrawerVisible(status);
          if (!status) props.reloadList();
        }}
        resvNo={resvNo}
      />

      <dl className={styles['partSettle-table-wrapper']}>
        <dt className={commonStyles['bill-block-title']}>
          {intl.get('bill_part_refund_wait_check')}
        </dt>
        <dd>
          <PartSettleTable dataList={selectedBills} />
        </dd>
      </dl>
      <PartSettleInfo info={partSettleInfo} infoLoading={infoLoading} />
      {renderPayDesk()}
    </Drawer>
  );
}

const mapStateToProps = (state) => {
  const { selectedBills, selectedBillIds, settlementAction } = state.bill;
  const { drawerVisible, infoLoading, partSettleInfo } = state.partSettle;
  return {
    selectedBills,
    selectedBillIds,
    drawerVisible,
    infoLoading,
    partSettleInfo,
    settlementAction,
  };
};

export default connect(mapStateToProps, {
  setSettlementAction,
  togglePartSettleDrawerVisible,
  initPartSettleInfo,
  resetPartSettleInfo,
})(PartSettle);
